import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert, Slide, Button } from '@mui/material';

const SnackbarContext = createContext();

// Slide transition for snackbar
const SlideTransition = (props) => <Slide {...props} direction="up" />;

export const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success', // 'success', 'error', 'warning', 'info'
    duration: 4000,
    action: null // { label: string, onClick: function }
  });

  const showSnackbar = useCallback((message, severity = 'success', duration = 4000) => {
    setSnackbar({
      open: true,
      message,
      severity,
      duration
    });
  }, []);

  const hideSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Convenience methods
  const showSuccess = useCallback((message, duration) => {
    showSnackbar(message, 'success', duration);
  }, [showSnackbar]);

  const showError = useCallback((message, duration) => {
    showSnackbar(message, 'error', duration);
  }, [showSnackbar]);

  const showWarning = useCallback((message, duration) => {
    showSnackbar(message, 'warning', duration);
  }, [showSnackbar]);

  const showInfo = useCallback((message, options = {}) => {
    const duration = options.duration !== undefined ? options.duration : (options.autoHideDuration !== undefined ? options.autoHideDuration : 4000);
    setSnackbar({
      open: true,
      message,
      severity: 'info',
      duration,
      action: options.action || null
    });
  }, []);

  return (
    <SnackbarContext.Provider value={{ showSnackbar, showSuccess, showError, showWarning, showInfo, hideSnackbar }}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.duration}
        onClose={hideSnackbar}
        TransitionComponent={SlideTransition}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={hideSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', boxShadow: 3 }}
          action={
            snackbar.action ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  snackbar.action.onClick();
                  hideSnackbar();
                }}
                sx={{ fontWeight: 'bold' }}
              >
                {snackbar.action.label}
              </Button>
            ) : undefined
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};
